import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

const AGENT_COLORS = { "k-create": "#00E5FF", "k-visual": "#8A52FF", "k-video": "#00E5FF", "k-research": "#34D399", "k-social": "#8A52FF", "k-insight": "#FBBF24", "core-swarm": "#F472B6" };

export default function AdminUsage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/usage").then((r) => setData(r.data)).catch(() => {});
  }, []);
  const timeline = data?.timeline?.map((x) => ({ ...x, date: x.date.slice(5) })) || [];

  return (
    <div className="p-8 max-w-[1600px] mx-auto">
      <div className="kr-overline text-[#8A52FF]">[ API USAGE ]</div>
      <h1 className="font-display text-4xl mt-2 tracking-tight" data-testid="admin-usage-title">Platform usage</h1>
      <p className="text-white/55 text-sm mt-1.5">Generations across all agents over the last two weeks.</p>

      <div className="mt-10 grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-8 kr-card p-6">
          <div className="kr-overline">[ 14-DAY GENERATIONS ]</div>
          <div className="h-64 mt-4 min-h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeline}>
                <XAxis dataKey="date" stroke="#52525B" fontSize={10} />
                <YAxis stroke="#52525B" fontSize={10} />
                <Tooltip contentStyle={{ background: "#0A0A0E", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 12 }} />
                <Line type="monotone" dataKey="count" stroke="#8A52FF" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-4 kr-card p-6">
          <div className="kr-overline">[ BY AGENT ]</div>
          <div className="h-64 mt-4 min-h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.by_agent || []} layout="vertical">
                <XAxis type="number" stroke="#52525B" fontSize={10} />
                <YAxis dataKey="agent" type="category" stroke="#52525B" fontSize={10} width={88} />
                <Tooltip contentStyle={{ background: "#0A0A0E", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 12 }} />
                <Bar dataKey="count">
                  {(data?.by_agent || []).map((d) => (
                    <Cell key={d.agent} fill={AGENT_COLORS[d.agent] || "#fff"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-5 kr-card p-6">
          <div className="kr-overline">[ BY KIND ]</div>
          <div className="mt-5 space-y-2">
            {(data?.by_kind || []).map((k) => {
              const max = Math.max(...(data?.by_kind || []).map((x) => x.count), 1);
              return (
                <div key={k.kind}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-white/70 capitalize">{k.kind}</span>
                    <span className="font-mono text-white/50">{k.count}</span>
                  </div>
                  <div className="h-1.5 bg-white/5 mt-1">
                    <div className="h-full bg-gradient-to-r from-[#00E5FF] to-[#8A52FF]" style={{ width: `${(k.count / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
            {(!data?.by_kind || data.by_kind.length === 0) && <div className="text-sm text-white/30">No generations yet.</div>}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7 kr-card p-6">
          <div className="kr-overline">[ TOP USERS ]</div>
          <div className="mt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/40 border-b border-white/5">
                  <th className="text-left p-2 font-mono text-[10px] tracking-wider uppercase">User</th>
                  <th className="text-left p-2 font-mono text-[10px] tracking-wider uppercase">Plan</th>
                  <th className="text-right p-2 font-mono text-[10px] tracking-wider uppercase">Generations</th>
                </tr>
              </thead>
              <tbody>
                {(data?.top_users || []).map((u) => (
                  <tr key={u.email || u.name} className="border-b border-white/5">
                    <td className="p-2"><div>{u.name}</div><div className="text-xs text-white/40">{u.email}</div></td>
                    <td className="p-2 capitalize">{u.plan}</td>
                    <td className="p-2 text-right font-mono">{u.count}</td>
                  </tr>
                ))}
                {(!data?.top_users || data.top_users.length === 0) && (
                  <tr><td colSpan={3} className="p-8 text-center text-white/40">No data yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
